import { useTranslations } from '@vocab/react';
import { Column, Columns, Stack, Text } from 'braid-design-system';
import React, {
  forwardRef,
  useRef,
  type Ref,
  type RefObject,
  useContext,
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { SECTION_PAY_DETAILS } from 'src/app/constants/pageSectionName';
import { usePayTypeTaxonomy } from 'src/app/taxonomies/usePayTypeTaxonomy';
import { PayDisplayText } from 'src/app/views/components/PayDisplayText';
import { PayDisplayToggle } from 'src/app/views/components/PayDisplayToggle';
import { PayRange } from 'src/app/views/components/PayRange/index';
import { PayType } from 'src/app/views/components/PayType';
import { EditFlowDataContext } from 'src/app/views/context/cajaFlowData';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { EditablePageSection } from 'src/app/views/sections/EditablePageSection';
import { ContentLoader } from 'src/app/views/shared/ContentLoader';
import type AppState from 'src/types/appState';
import type { PayRangeRef } from 'src/types/formFields';
import type { SectionProps, SectionRef } from 'src/types/sections';

import translations from './.vocab';
import { getPayRange } from './utils/getPayRange';

export const Read = () => {
  const {
    displayPay,
    minSalary,
    maxSalary,
    payType,
    salaryCurrency,
    salaryText,
  } = useSelector(
    (state: AppState) => ({
      displayPay: state.formData.displayPay,
      minSalary: state.formData.minSalary,
      maxSalary: state.formData.maxSalary,
      payType: state.formData.payType,
      salaryCurrency: state.formData.salaryCurrency,
      salaryText: state.formData.salaryText,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const { loading } = useContext(EditFlowDataContext);

  const { getPayTypeText } = usePayTypeTaxonomy();

  const currency = salaryCurrency ?? 'USD';
  const locale = useConfig().getLocale();

  return (
    <Stack space="large">
      <Columns space="small">
        <Column width="1/4">
          <Text weight="strong">
            {t('pay-details-section-read-pay-type-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['50%']} offset>
            <Text>{getPayTypeText(payType)}</Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('pay-details-section-read-pay-range-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['75%']} offset>
            <Text>
              {getPayRange({
                min: minSalary,
                max: maxSalary,
                currency,
                locale,
              })}
            </Text>
          </ContentLoader>
        </Column>
      </Columns>
      <Columns space="xsmall">
        <Column width="1/4">
          <Text weight="strong">
            {t('pay-details-section-read-hidden-salary-label')}
          </Text>
        </Column>
        <Column>
          <ContentLoader loading={loading} lines={['10%']} offset>
            <Text>
              {displayPay
                ? t('pay-details-section-read-hidden-salary-false')
                : t('pay-details-section-read-hidden-salary-true')}
            </Text>
          </ContentLoader>
        </Column>
      </Columns>
      {displayPay && !loading && (
        <Columns space="xsmall">
          <Column width="1/4">
            <Text weight="strong">
              {t('pay-details-section-read-pay-display-text-label')}
            </Text>
          </Column>
          <Column>
            <ContentLoader loading={loading} lines={['10%']} offset>
              <Text>{salaryText}</Text>
            </ContentLoader>
          </Column>
        </Columns>
      )}
    </Stack>
  );
};

interface EditProps {
  refs: {
    payRangeRef: RefObject<any>;
    payDisplayTextRef: RefObject<any>;
  };
}

export const Edit = ({ refs }: EditProps) => (
  <Stack space="medium">
    <PayType />
    <PayRange ref={refs.payRangeRef} />
    <PayDisplayToggle />
    <PayDisplayText ref={refs.payDisplayTextRef} />
  </Stack>
);

export const PayDetailsSection = forwardRef(
  (_props: SectionProps, ref: Ref<SectionRef>) => {
    const payRangeRef = useRef<PayRangeRef>();
    const payDisplayTextRef = useRef<any>();

    const { t } = useTranslations(translations);

    return (
      <EditablePageSection
        title={t('pay-details-section-title')}
        name={SECTION_PAY_DETAILS}
        ref={ref}
        fields={[payRangeRef, payDisplayTextRef]}
        read={<Read />}
        edit={<Edit refs={{ payRangeRef, payDisplayTextRef }} />}
      />
    );
  },
);
